import { Link, useLocation } from "react-router-dom";
import { EMAIL, PHONE_DISPLAY, PHONE_HREF, SOCIALS } from "@/content/site";
import { QuoteRibbon } from "@/components/site/RotatingQuote";
import { trackCTA } from "@/lib/tracking";
import { cn } from "@/lib/utils";
import { Download } from "lucide-react";

export const SiteFooter = () => {
  const { pathname } = useLocation();
  const onAdvocacy = pathname === "/advocacy" || pathname.startsWith("/advocacy/");

  return (
  <>
    <QuoteRibbon />
    <footer className="border-t border-border bg-navy-deep">

    <div className="container py-16">
      <div className="grid md:grid-cols-3 gap-10">
        <div>
          <div className="font-display font-bold uppercase tracking-[0.18em] text-white">
            DrSully<span className="text-orange">Speaks</span>
          </div>
          <p className="text-silver text-sm mt-4 max-w-xs leading-relaxed">
            He Speaks Organic Coaching & Consulting LLC · San Antonio, TX. Operating globally.
          </p>
          <p className="text-gold font-display uppercase tracking-[0.2em] text-xs mt-6">
            "If it doesn't make sense — make change."
          </p>
          <Link
            to="/app"
            onClick={() => trackCTA("app_download", { surface: "footer" })}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-cta px-5 py-3 font-display uppercase tracking-[0.2em] text-xs text-white shadow-cta transition-all hover:brightness-110 active:scale-[0.98]"
          >
            <Download aria-hidden="true" className="h-4 w-4" />
            Download the App
          </Link>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Ecosystem</div>
          <ul className="space-y-3 text-sm text-silver">
            <li><Link to="/books" className="hover:text-white transition-colors">Books &amp; Resources</Link></li>
            <li><Link to="/ebooks" className="hover:text-white transition-colors">eBooks &amp; Workbooks (Instant PDF)</Link></li>
            <li><Link to="/blog" className="hover:text-white transition-colors">Insights &amp; Essays</Link></li>
            <li><a href="https://saysosociety.com/collections/drsullyspeaks-merch" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">DrSullySpeaks Merch</a></li>
            <li><a href="https://saysosociety.com/collections" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Say So Society — Global Co.</a></li>
            <li><a href="https://stan.store/DrSullySpeaks" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Stan Store</a></li>
            <li><a href="https://open.spotify.com/show/6t5G1oDtkligs6wi4iGgvZ" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Podcast — Honest Kingdom Voice</a></li>
            <li><a href="https://music.youtube.com/channel/UClW-Fv3VHRzaB5YQtkyhuGA" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Music — @DrSullySpeaks</a></li>
            <li><a href="https://www.anchoredsa.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Anchored SA — Ministry</a></li>
            <li><a href="https://substack.com/@drsullyspeaks" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Substack</a></li>
            <li><Link to="/app" className="hover:text-white transition-colors">Download the App — MAKE CHANGE NOW</Link></li>
            <li><Link to="/links" className="hover:text-white transition-colors">All Links</Link></li>
            <li><Link to="/scan" className="hover:text-white transition-colors">Scans · Opps-Finder</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Contact</div>
          <a
            href={PHONE_HREF}
            onClick={() => trackCTA("phone_click", { surface: "footer" })}
            className="group inline-flex flex-col border border-gold/40 bg-gold/5 hover:bg-gold/10 hover:border-gold active:bg-gold/15 active:scale-[0.99] transition-all px-4 py-3 rounded-sm mb-4 touch-manipulation"
          >
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold/80">Call Now · 24/7 Booking Line</span>
            <span className="font-display text-white text-lg tracking-[0.1em] mt-0.5">{PHONE_DISPLAY}</span>
          </a>
          <ul className="space-y-3 text-sm text-silver">
            <li><a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors">{EMAIL}</a></li>
            <li><Link to="/inquire" className="hover:text-white transition-colors">Start an Inquiry →</Link></li>
          </ul>

          <Link
            to="/advocacy"
            onClick={() => trackCTA("advocacy_cta", { surface: "footer" })}
            aria-current={onAdvocacy ? "page" : undefined}
            className={cn(
              "mt-5 group inline-flex items-center gap-3 px-4 py-3 min-h-11 rounded-sm border bg-[image:var(--gradient-advocacy)] shadow-[var(--shadow-advocacy)] hover:brightness-110 active:scale-[0.99] transition-all touch-manipulation w-full outline-none focus-visible:ring-2 focus-visible:ring-advocacy-glow focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep",
              onAdvocacy
                ? "border-gold ring-2 ring-gold/60 ring-offset-2 ring-offset-navy-deep brightness-110"
                : "border-advocacy/60",
            )}
            aria-label={
              onAdvocacy
                ? "Special Needs Advocacy — Full-Scope Advocacy Partner (current page)"
                : "Special Needs Advocacy — Full-Scope Advocacy Partner"
            }
          >

            <span aria-hidden="true" className="text-white text-lg">✦</span>
            <span className="flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-[0.28em] text-white">Special Needs</span>
              <span className="font-display uppercase tracking-[0.18em] text-white text-sm">Advocacy · FSAP →</span>
            </span>

          </Link>
        </div>
      </div>

      {/* SEO — Sitemap / Explore */}
      <div className="mt-12">
        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Explore</div>
        <nav aria-label="Sitemap" className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm text-silver">
          {[
            { to: "/", label: "Home" },
            { to: "/about", label: "About Dr. Sully" },
            { to: "/speaking", label: "Keynote Speaking" },
            { to: "/books", label: "Books & Resources" },
            { to: "/ebooks", label: "eBooks & Workbooks" },
            { to: "/framework", label: "The MAKECHANGE Framework" },
            { to: "/reviews", label: "Client Reviews" },
            { to: "/scan", label: "Opps-Finder Diagnostic" },
            { to: "/inquire", label: "Work With Dr. Sully" },
            { to: "/honest-kingdom-voice", label: "Honest Kingdom Voice" },
            { to: "/free", label: "Free Resources" },
            { to: "/blog", label: "Insights & Essays" },
            { to: "/links", label: "All Links" },
          ].map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-white transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>
      </div>

      <div className="mt-12">
        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Connect</div>
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3 text-sm text-silver">
          {SOCIALS.map((s) => (
            <li key={s.label}>
              <a
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                <span className="block text-[10px] uppercase tracking-[0.25em] text-gold/70">{s.label}</span>
                <span>{s.handle}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="gold-divider opacity-40 mt-12 mb-6" />

      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">The MAKECHANGE Framework</div>
        <ul className="flex flex-wrap gap-x-3 gap-y-2 text-[11px] uppercase tracking-[0.18em] text-silver">
          {[
            "Momentum",
            "Authority",
            "Kingdom-Focus",
            "Equity",
            "Clarity",
            "Healing",
            "Action",
            "Navigation",
            "Growth",
            "Empowerment",
          ].map((pillar, i) => (
            <li key={pillar} className="flex items-center gap-3">
              <span className="text-white/90">
                <span className="text-gold font-display mr-1.5">{pillar[0]}</span>
                {pillar.slice(1)}
              </span>
              {i < 9 && <span className="text-gold/40">·</span>}
            </li>
          ))}
        </ul>
      </div>

      <div className="gold-divider opacity-40 mb-6" />

      {/* Plain-English legal disclaimer (full version at /legal). */}
      <div className="mb-6 text-[11px] leading-relaxed text-silver/70 max-w-4xl">
        <strong className="text-silver/90">Disclaimer:</strong> Content on this site is for
        educational and informational purposes only and is not medical, mental-health, legal,
        financial, or investment advice. Coaching is not therapy. Results, testimonials, and case
        studies reflect specific clients and are not guarantees of your outcome. AI guidance is a
        starting point — validate before deploying. Read the full{" "}
        <Link to="/legal" className="text-gold underline underline-offset-4">
          Legal & Disclaimers
        </Link>
        .
      </div>

      <div className="flex flex-col md:flex-row justify-between gap-3 text-xs text-silver/70">
        <span>© {new Date().getFullYear()} DrSullySpeaks™. All rights reserved.</span>
        <span className="flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/legal" className="hover:text-white transition-colors">
            Legal & Disclaimers
          </Link>
          <span>Faith-informed. Business-driven. Kingdom-focused.</span>
        </span>
      </div>
    </div>
  </footer>
  </>
  );
};

